import { useClientStore } from "@/store/client";
import { ApiClient } from "@/services/clients";
import { useFabriqStore } from "@/store/fabriq";
import { useTeamsStore } from "@/store/teams";
import { useTicketsStore } from "@/store/tickets";
import { useTasksStore } from "@/store/tasks";
import { useFilesStore } from "@/store/files";
import { useCommentsStore } from "@/store/comments";
import { useNotificationsStore } from "@/store/notifications";
import {
  buildMessagingSocket,
  buildReconnectingWebSocket,
} from "./socket-builder";
import type { SocketPropertiesMergedEventHandler } from "../composables/useHandleSocketPropertiesMergedEvent";
import { SocketTicketTemplateEventsHandler } from "@/composables/useHandleSocketTicketTemplateEvents";

let teams = false;
let client: number | null = null;
let user: number | null = null;

const RELOAD_TIMER = 5000;
const UPDATED_TIMER = 20000;
const clientEvents = [
  "teamChanged",
  "clientChanged",
  "groupChanged",
  "categoryChanged",
  "labelChanged",
  "useChanged",
];
let clientEventsTimer: NodeJS.Timer | null = null;
const ticketEventsTimers: Map<number, NodeJS.Timer> = new Map();
const ticketUpdatedTimers: Map<number, NodeJS.Timer> = new Map();

const doReloadTicket = (id: number): NodeJS.Timer => {
  return setTimeout(() => {
    // eslint-disable-next-line fabriq-webapp/vue-composable-rules
    const ticketsStore = useTicketsStore();
    ticketsStore.load(id);
    ticketEventsTimers.delete(id);
  }, RELOAD_TIMER);
};

const doTicketUpdated = (id: number): NodeJS.Timer => {
  return setTimeout(() => {
    ticketUpdatedTimers.delete(id);
  }, UPDATED_TIMER);
};

export const ticketUpdatedByMe = (id: number) => {
  if (ticketUpdatedTimers.has(id)) {
    const timer = ticketUpdatedTimers.get(id);
    if (timer) clearTimeout(timer);
  }
  ticketUpdatedTimers.set(id, doTicketUpdated(id));
};

const doNotificationChanged = (id: number): void => {
  // eslint-disable-next-line fabriq-webapp/vue-composable-rules
  const notificationsStore = useNotificationsStore();
  try {
    notificationsStore.load(id);
  } catch (e) {}
};

const reloadTicket = (id: number) => {
  if (ticketUpdatedTimers.has(id)) return;
  if (ticketEventsTimers.has(id)) {
    const timer = ticketEventsTimers.get(id);
    if (timer) clearTimeout(timer);
  }
  ticketEventsTimers.set(id, doReloadTicket(id));
};

let handleSocketPropertiesMergedEvent: SocketPropertiesMergedEventHandler;

export const setSocketPropertiesMergedEventHandler = (
  handler: SocketPropertiesMergedEventHandler
) => {
  handleSocketPropertiesMergedEvent = handler;
};

let handleSocketTicketTemplateEvents: SocketTicketTemplateEventsHandler;

export const setSocketTicketTemplateEventsHandler = (
  handler: SocketTicketTemplateEventsHandler
) => {
  handleSocketTicketTemplateEvents = handler;
};

export default async (url: string) => {
  const token = await ApiClient.getFreshToken();
  const socket = buildMessagingSocket(
    buildReconnectingWebSocket(url, token?.accessToken)
  );

  socket.onopen = async () => {
    const token = await ApiClient.getFreshToken();
    // eslint-disable-next-line fabriq-webapp/vue-composable-rules
    const clientStore = useClientStore();
    // eslint-disable-next-line fabriq-webapp/vue-composable-rules
    const fabriqStore = useFabriqStore();
    // eslint-disable-next-line fabriq-webapp/vue-composable-rules
    const teamsStore = useTeamsStore();
    if (!user && fabriqStore.user?.id) {
      user = fabriqStore.user?.id;
      socket.emit("userSubscribe", { userId: user, token: token?.accessToken });
    }
    if (!client && clientStore.client?.id) {
      client = clientStore.client?.id;
      if (!client) return;
      socket.emit("clientSubscribe", {
        clientId: client,
        token: token?.accessToken,
      });
    }
    if (!teams) {
      teams = true;
      const teamIds = teamsStore.collection.map((t: any) => t.id);
      fabriqStore.setTeams(teamIds);
    }
  };

  socket.onclose = () => {
    user = null;
    client = null;
    teams = false;
  };

  socket.onmessage = (ev: MessageEvent) => {
    try {
      const data = JSON.parse(ev.data);
      const eventName = data.event;
      const params = data.args;

      if (eventName.includes("Changed")) {
        switch (eventName) {
          case "ticketChanged": {
            // eslint-disable-next-line fabriq-webapp/vue-composable-rules
            const fabriqStore = useFabriqStore();
            if (fabriqStore.user?.id === params[1]) return;
            return reloadTicket(params[0]);
          }
          case "commentChanged":
            if (params.length < 3 || params[1] !== "ticket") return;
            return reloadTicket(params[2]);
          case "taskChanged":
          case "ticketfileChanged":
            return reloadTicket(params[0]);
          case "notificationChanged": {
            doNotificationChanged(params[0]);
          }
          case "ticketTemplateChanged": {
            handleSocketTicketTemplateEvents();
            break;
          }
        }
        if (clientEvents.includes(eventName)) {
          if (clientEventsTimer) clearTimeout(clientEventsTimer);
          clientEventsTimer = setTimeout(() => {
            // eslint-disable-next-line fabriq-webapp/vue-composable-rules
            const fabriqStore = useFabriqStore();
            // eslint-disable-next-line fabriq-webapp/vue-composable-rules
            const teamsStore = useTeamsStore();
            fabriqStore.loadUserData(teamsStore);
          }, RELOAD_TIMER);
        }
      } else if (eventName.includes("Delete")) {
        switch (eventName) {
          case "ticketDeleted": {
            // eslint-disable-next-line fabriq-webapp/vue-composable-rules
            const ticketsStore = useTicketsStore();
            const ticket = ticketsStore.collection.find(
              (t: any) => t.id === params[0]
            );
            if (!ticket) return;
            return ticketsStore.remove(ticket);
          }
          case "taskDeleted": {
            // eslint-disable-next-line fabriq-webapp/vue-composable-rules
            const tasksStore = useTasksStore();
            const task = tasksStore.collection.find(
              (t: any) => t.id === params[0]
            );
            if (!task) return;
            return tasksStore.remove(task);
          }
          case "commentDeleted": {
            // eslint-disable-next-line fabriq-webapp/vue-composable-rules
            const commentsStore = useCommentsStore();
            const comment = commentsStore.collection.find(
              (t: any) => t.id === params[0]
            );
            if (!comment) return;
            return commentsStore.remove(comment);
          }
          case "ticketfileDeleted": {
            if (params.length < 2) return;
            // eslint-disable-next-line fabriq-webapp/vue-composable-rules
            const filesStore = useFilesStore();
            const file = filesStore.collection.find(
              (f: any) => f.id === params[1]
            );
            if (!file) return;
            return filesStore.remove(file);
          }
          case "ticketTemplateDeleted": {
            handleSocketTicketTemplateEvents();
            break;
          }
        }
        if (clientEvents.includes(eventName)) {
          if (clientEventsTimer) clearTimeout(clientEventsTimer);
          clientEventsTimer = setTimeout(() => {
            // eslint-disable-next-line fabriq-webapp/vue-composable-rules
            const fabriqStore = useFabriqStore();
            // eslint-disable-next-line fabriq-webapp/vue-composable-rules
            const teamsStore = useTeamsStore();
            fabriqStore.loadUserData(teamsStore);
          }, RELOAD_TIMER);
        }
      } else if (eventName === "propertiesMerged") {
        if (handleSocketPropertiesMergedEvent)
          handleSocketPropertiesMergedEvent(params);
      }
    } catch (e) {}
  };

  return socket;
};
